import { inferRouteObject } from 'react-router-typed-object';

import { Navigate } from '@/js/libs/router';
import { and, not, PrivateRoute, Roles } from '@/js/libs/use-auth';

import LegacyPathRedirect from 'src/components/LegacyPathRedirect.tsx';
import type { Condition } from 'src/components/RoleBasedRedirector.tsx';
import RoleBasedRedirector from 'src/components/RoleBasedRedirector.tsx';
import Settings from 'src/scenes/Settings/index.ts';
import Status from 'src/scenes/Status/index.ts';
import {
  companySettingsRouteRoles,
  productCatalogueRouteRoles,
  programmaticMappingRouteRoles,
  settingsRouteRoles,
} from './sectionRoles.ts';

const index = true;

const settingsConditions: Condition[] = [
  { roles: and(Roles.MANAGE_ACCOUNTS, not(Roles.POSTADMANAGER)), to: './data-import' },
  { roles: Roles.CONTRACT, to: './contracts' },
  { roles: Roles.MANAGE_REFERENCE_PER_AD_MEDIA, to: './reference-per-admedia' },
  { roles: Roles.SPECIAL_DISCOUNTS, to: './special-discounts' },
  { roles: Roles.POSTNETZ, to: './partner-filial-management' },
  // A bookmark on bare /settings predates the split, so it still has to land somewhere
  // for a user whose sections all moved out.
  { roles: productCatalogueRouteRoles, to: '/product-catalogue' },
  { roles: companySettingsRouteRoles, to: '/company-settings' },
  { roles: programmaticMappingRouteRoles, to: '/programmatic-mapping' },
];

export const settingsRouteObject = inferRouteObject({
  path: 'settings',
  children: [
    // Sections that moved out of Settings keep working from bookmarked URLs; the section they
    // moved to applies its own role check, so these are deliberately outside the role gate below.
    { path: 'digital-products/*', element: <LegacyPathRedirect to='/product-catalogue/digital-products' /> },
    { path: 'print-products/*', element: <LegacyPathRedirect to='/product-catalogue/print-products' /> },
    { path: 'point-of-sale-products/*', element: <LegacyPathRedirect to='/product-catalogue/point-of-sale-products' /> },
    { path: 'consulting-products/*', element: <LegacyPathRedirect to='/product-catalogue/consulting-products' /> },
    { path: 'position-groups/*', element: <LegacyPathRedirect to='/product-catalogue/position-groups' /> },
    { path: 'price-lists/*', element: <LegacyPathRedirect to='/product-catalogue/price-lists' /> },
    { path: 'analog-products/*', element: <LegacyPathRedirect to='/product-catalogue/analog-products' /> },
    { path: 'radio-products/*', element: <LegacyPathRedirect to='/product-catalogue/radio-products' /> },
    { path: 'tv-products/*', element: <LegacyPathRedirect to='/product-catalogue/tv-products' /> },
    { path: 'sales-units/*', element: <LegacyPathRedirect to='/company-settings/sales-units' /> },
    { path: 'websites/*', element: <LegacyPathRedirect to='/company-settings/websites' /> },
    { path: 'passendo/*', element: <LegacyPathRedirect to='/company-settings/passendo' /> },
    { path: 'brands/*', element: <LegacyPathRedirect to='/company-settings/brands' /> },
    { path: 'labels/*', element: <LegacyPathRedirect to='/company-settings/labels' /> },
    { path: 'campaign-types/*', element: <LegacyPathRedirect to='/company-settings/campaign-types' /> },
    { path: 'campaign-types-v2/*', element: <LegacyPathRedirect to='/company-settings/campaign-types-v2' /> },
    { path: 'industries/*', element: <LegacyPathRedirect to='/company-settings/industries' /> },
    { path: 'account-types/*', element: <LegacyPathRedirect to='/company-settings/account-types' /> },
    { path: 'company-settings/*', element: <LegacyPathRedirect to='/company-settings/settings' /> },
    { path: 'xandr-programmatic/*', element: <LegacyPathRedirect to='/programmatic-mapping/xandr-programmatic' /> },
    { path: 'google-programmatic/*', element: <LegacyPathRedirect to='/programmatic-mapping/google-programmatic' /> },
    { path: 'adomik-programmatic/*', element: <LegacyPathRedirect to='/programmatic-mapping/adomik-programmatic' /> },
    {
      index,
      element: <RoleBasedRedirector conditions={settingsConditions} noAccessElement={<Status.NotFound />} />,
    },
    {
      element: <PrivateRoute roles={settingsRouteRoles} noAccessComponent={Status.NotFound} />,
      children: [
        {
          Component: Settings.Helmet,
          children: [
            {
              element: <PrivateRoute roles={and(Roles.MANAGE_ACCOUNTS, not(Roles.POSTADMANAGER))} noAccessComponent={Status.NotFound} />,
              children: [
                {
                  path: 'data-import',
                  Component: Settings.DataImport,
                },
              ],
            },
            {
              path: 'partner-filial-management',
              element: <PrivateRoute roles={Roles.POSTNETZ} noAccessComponent={Status.NotFound} />,
              children: [
                { index, element: <Navigate to='./partner-branches' replace /> },
                { path: 'partner-branches', Component: Settings.PartnerFilialManagement.PartnerBranches },
              ],
            },
            {
              path: 'reference-per-admedia',
              element: <PrivateRoute roles={Roles.MANAGE_REFERENCE_PER_AD_MEDIA} noAccessComponent={Status.NotFound} />,
              children: [
                { index, Component: Settings.ReferencePerAdmedia.List },
                { path: 'create', Component: Settings.ReferencePerAdmedia.Edit },
                { path: 'edit/:id', Component: Settings.ReferencePerAdmedia.Edit },
              ],
            },
            {
              path: 'special-discounts',
              element: <PrivateRoute roles={Roles.SPECIAL_DISCOUNTS} noAccessComponent={Status.NotFound} />,
              children: [{ index, Component: Settings.SpecialDiscounts }],
            },
            {
              path: 'contracts',
              element: <PrivateRoute roles={Roles.CONTRACT} noAccessComponent={Status.NotFound} />,
              children: [
                { index, Component: Settings.Contracts.ContractsList },
                { path: 'create', Component: Settings.Contracts.ContractForm },
                { path: 'edit/:discountContractId', Component: Settings.Contracts.ContractForm },
                { path: ':discountContractId/history', Component: Settings.Contracts.ContractHistory },
                { path: ':discountContractId/progress', Component: Settings.Contracts.ContractProgress },
              ],
            },
          ],
        },
        {
          path: 'contract-settlements',
          element: <PrivateRoute roles={Roles.CONTRACT} noAccessComponent={Status.NotFound} />,
          children: [
            { index, Component: Settings.Contracts.ContractSettlementsList },
            { path: ':discountContractId', Component: Settings.Contracts.ContractSettlements },
          ],
        },
      ],
    },
  ],
});
